import { useState } from "react";
import {
  Dismiss24Regular,
  Checkmark24Regular,
  ZoomIn20Regular,
  ZoomOut20Regular,
} from "@fluentui/react-icons";

interface AvatarCropModalProps {
  imageUrl: string | null;
  onComplete: (croppedImage: string) => void;
  onCancel: () => void;
  show: boolean;
}

export function AvatarCropModal({
  imageUrl,
  onComplete,
  onCancel,
  show,
}: AvatarCropModalProps) {
  const [zoom, setZoom] = useState(1);

  if (!show || !imageUrl) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div
        className="bg-card w-full max-w-2xl shadow-lg overflow-hidden flex flex-col"
        style={{
          borderRadius: "var(--radius-card)",
          maxHeight: "90vh",
        }}
      >
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-card-foreground flex items-center gap-2">
            Ajustar Fotografia
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-accent transition-colors"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            <Dismiss24Regular className="w-6 h-6 text-foreground" />
          </button>
        </div>

        <div className="relative bg-muted" style={{ height: "400px" }}>
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            <img
              src={imageUrl}
              alt="Pré-visualização"
              className="max-w-none"
              style={{
                transform: `scale(${zoom})`,
                transition: "transform 0.2s ease",
              }}
            />
          </div>
        </div>

        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <ZoomOut20Regular className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            <div className="flex-1 relative flex items-center">
              <div
                className="absolute left-0 right-0 h-[6px] bg-muted pointer-events-none"
                style={{
                  borderRadius: "var(--radius)",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
              <input
                type="range"
                min={0.6}
                max={2}
                step={0.1}
                value={zoom}
                onChange={(event) => setZoom(Number(event.target.value))}
                className="zoom-slider"
              />
            </div>
            <ZoomIn20Regular className="w-5 h-5 text-muted-foreground flex-shrink-0" />
          </div>
          <p className="text-xs text-muted-foreground mt-3 text-center">
            Ajuste a ampliação para enquadrar a fotografia
          </p>
        </div>

        <style>{`
          .zoom-slider {
            position: relative;
            width: 100%;
            height: 20px;
            -webkit-appearance: none;
            appearance: none;
            background: transparent;
            outline: none;
            cursor: pointer;
            z-index: 10;
          }

          .zoom-slider::-webkit-slider-track {
            background: transparent;
            border: none;
          }

          .zoom-slider::-moz-range-track {
            background: transparent;
            border: none;
          }

          .zoom-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 20px;
            height: 20px;
            background: var(--primary);
            border-radius: var(--radius-full);
            cursor: pointer;
            transition: all 0.2s ease;
            border: 3px solid var(--card);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          .zoom-slider::-moz-range-thumb {
            width: 20px;
            height: 20px;
            background: var(--primary);
            border-radius: var(--radius-full);
            cursor: pointer;
            transition: all 0.2s ease;
            border: 3px solid var(--card);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }

          .zoom-slider::-webkit-slider-thumb:hover {
            transform: scale(1.1);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          }

          .zoom-slider::-moz-range-thumb:hover {
            transform: scale(1.1);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          }

          .zoom-slider::-webkit-slider-thumb:active {
            transform: scale(1.05);
          }

          .zoom-slider::-moz-range-thumb:active {
            transform: scale(1.05);
          }
        `}</style>

        <div className="p-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-secondary hover:bg-accent border border-border transition-colors flex items-center gap-2 min-h-[44px]"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            <Dismiss24Regular className="w-5 h-5" />
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onComplete(imageUrl)}
            className="px-4 py-2 bg-primary text-primary-foreground hover:opacity-90 transition-colors flex items-center gap-2 min-h-[44px]"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            <Checkmark24Regular className="w-5 h-5" />
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}
